import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useVersion } from '../context/VersionContext';
import './UpdateNotification.css';

/**
 * UpdateNotification — Gate de atualização BLOQUEANTE.
 *
 * Quando o version.json publicado com o frontend reporta uma versão diferente
 * da que está carregada no navegador, cobrimos a tela inteira e impedimos o uso
 * do sistema até recarregar. A ideia é que ninguém opere numa versão antiga —
 * foi o que deixou a aprovação da diretoria falhando silenciosamente no celular.
 *
 * Segurança contra loop: se o reload não resolver (ex.: backend já anunciou a
 * versão nova mas o bundle do frontend ainda não foi publicado), paramos de
 * recarregar sozinhos depois de algumas tentativas e passamos a exigir clique
 * manual — senão o aparelho ficaria recarregando em loop.
 */

// Segundos até a atualização automática quando uma versão nova é detectada.
const AUTO_RELOAD_SECONDS = 20;
// Tentativas de reload automático para a MESMA versão-alvo antes de desistir do
// automático e exigir ação manual (evita loop de recarga).
const MAX_AUTO_ATTEMPTS = 2;

const TARGET_KEY = 'updateGate.target';
const ATTEMPT_KEY = 'updateGate.attempts';

function lerTentativas(target) {
  try {
    if (sessionStorage.getItem(TARGET_KEY) !== String(target)) return 0;
    return parseInt(sessionStorage.getItem(ATTEMPT_KEY) || '0', 10) || 0;
  } catch {
    return 0;
  }
}

function registrarTentativa(target) {
  try {
    const atual = lerTentativas(target);
    sessionStorage.setItem(TARGET_KEY, String(target));
    sessionStorage.setItem(ATTEMPT_KEY, String(atual + 1));
  } catch {
    /* sessionStorage indisponível — segue sem contagem */
  }
}

function limparTentativas() {
  try {
    sessionStorage.removeItem(TARGET_KEY);
    sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    /* noop */
  }
}

export function UpdateNotification() {
  const { localVersion, serverVersion, isUpdateAvailable } = useVersion();
  const [segundos, setSegundos] = useState(AUTO_RELOAD_SECONDS);
  const [atualizando, setAtualizando] = useState(false);
  const btnRef = useRef(null);

  // Já tentamos recarregar para esta versão vezes demais? Então não insistimos
  // no automático — provavelmente o bundle novo ainda não está no servidor.
  const tentativasEsgotadas =
    isUpdateAvailable && lerTentativas(serverVersion) >= MAX_AUTO_ATTEMPTS;

  const forcarAtualizacao = useCallback(async () => {
    if (atualizando) return;
    setAtualizando(true);

    // 1) Limpa o Cache Storage do PWA (best-effort).
    try {
      if ('caches' in window) {
        const chaves = await caches.keys();
        await Promise.all(chaves.map((k) => caches.delete(k)));
      }
    } catch {
      /* segue mesmo assim */
    }

    // 2) Pede ao service worker em espera para assumir imediatamente.
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
      }
    } catch {
      /* idem */
    }

    // 3) Marca a tentativa (anti-loop) e recarrega com cache-bust.
    //    location.replace não empilha no histórico: o botão "voltar" não
    //    devolve o usuário para a versão antiga.
    registrarTentativa(serverVersion);
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());
    window.location.replace(url.toString());
  }, [atualizando, serverVersion]);

  // Sem atualização pendente: zera a contagem de tentativas.
  useEffect(() => {
    if (!isUpdateAvailable) limparTentativas();
  }, [isUpdateAvailable]);

  // Trava o scroll do fundo enquanto o gate está ativo.
  useEffect(() => {
    if (!isUpdateAvailable) return undefined;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [isUpdateAvailable]);

  // Move o foco para o botão ao abrir (bloqueia navegação por teclado no fundo).
  useEffect(() => {
    if (isUpdateAvailable && btnRef.current) btnRef.current.focus();
  }, [isUpdateAvailable]);

  // Contagem regressiva para o reload automático. Não roda se as tentativas
  // já se esgotaram (aí só o clique manual atualiza).
  useEffect(() => {
    if (!isUpdateAvailable || tentativasEsgotadas) return undefined;
    setSegundos(AUTO_RELOAD_SECONDS);
    const id = setInterval(() => {
      setSegundos((s) => {
        if (s <= 1) {
          clearInterval(id);
          forcarAtualizacao();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isUpdateAvailable, tentativasEsgotadas, forcarAtualizacao]);

  if (!isUpdateAvailable) return null;

  return ReactDOM.createPortal(
    <div
      className="update-gate"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="update-gate-title"
      aria-describedby="update-gate-text"
    >
      <div className="update-gate-card">
        <div className="update-gate-icon" aria-hidden="true">↻</div>

        <h2 id="update-gate-title" className="update-gate-title">
          Nova versão disponível
        </h2>

        <p id="update-gate-text" className="update-gate-text">
          Uma atualização do sistema foi publicada. Para continuar é preciso
          recarregar — a versão antiga não pode mais ser usada.
        </p>

        <div className="update-gate-versions" aria-hidden="true">
          <span className="update-gate-vfrom">
            {localVersion ? `v${localVersion}` : 'versão atual'}
          </span>
          <span className="update-gate-arrow">→</span>
          <span className="update-gate-vto">
            {serverVersion ? `v${serverVersion}` : 'nova versão'}
          </span>
        </div>

        <button
          ref={btnRef}
          type="button"
          className="update-gate-btn"
          onClick={forcarAtualizacao}
          disabled={atualizando}
        >
          {atualizando
            ? 'Atualizando…'
            : tentativasEsgotadas
            ? 'Tentar novamente'
            : 'Atualizar agora'}
        </button>

        {tentativasEsgotadas ? (
          <p className="update-gate-hint update-gate-hint--warn">
            A nova versão ainda está sendo publicada. Aguarde alguns instantes e
            toque em “Tentar novamente”. Se persistir, avise o suporte.
          </p>
        ) : (
          <p className="update-gate-hint">
            {atualizando
              ? 'Recarregando o sistema…'
              : `Atualização automática em ${segundos}s. Salve o que estiver fazendo antes.`}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}
